// React Components
import * as React from 'react';
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Material UI Components
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import AnnouncementIcon from '@mui/icons-material/Announcement';

// Audit Vault Components
import ProgressBar from "../common/ProgressBar";
import AccessDenied from "../common/AccessDenied";
import TenantDeleteConfirmationDialog from "./TenantDeleteConfirmationDialog";
import ServiceIsDown from "../common/ServiceIsDown";

// Audit Vault Utilities
import { verifyUserCanAccessTenantData } from "../../utilities/common-user-utils";
import { formatDate } from "../../utilities/common-date-utils";
import { getTenantById } from "../../utilities/common-tenant";

export default function TenantDetails(props) 
{
    const navigate = useNavigate();
    const { user, tenantId } = props;

    const [loading, setLoading] = useState(true);
    const [loadingMsg, setLoadingMsg] = useState("loading ...");
    const [tenant, setTenant] = useState("");
    const [company, setCompany] = useState("");
    const [accessDenied, setAccessDenied] = useState(false);
    const [serviceIsDownError, setServiceIsDownError] = useState(false);

    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [objectIdToDelete, setObjectIdToDelete] = useState(null); 

    // For snackbar notification UI element.  Company deleted notification.
    const [openSnackbarNotification, setOpenSnackbarNotification] = useState(false);  // Add React hook to add state to a functional component.  It returns the current state, and a function to update it.

    // For snackbar close action.
  const handleSnackbarNotificationClose = (event, reason) => {
    if (reason === 'clickaway') {
        return;
    }
    setOpenSnackbarNotification(false);
  }

  // For snackbar notification action.
  const openSnackbarNotificationAction = (
    <React.Fragment>
        <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleSnackbarNotificationClose}
        >
            <CloseIcon fontSize="small" />
        </IconButton>
    </React.Fragment>
)


    async function fetchData() {
        if(loading && user)
        {
            try{
                if (verifyUserCanAccessTenantData(user, tenantId))
                {
                    var response = await getTenantById(tenantId);
                    if (response) 
                    {
                        setTenant(response);
                    }
                }
                else
                {
                    setAccessDenied(true);
                }
            }
            catch (e)
            {
                setServiceIsDownError(true);
                console.log(e);
            }
            finally{
                setLoading(false);
            }
        }
    }

    useEffect(() => {
        fetchData();
    }, []);


    const handleDeleteClick = (value) => {
        setObjectIdToDelete(value); 
        setDeleteDialogOpen(true);
      };
    
    const handleDeleteConfirm = async () => {
    if (objectIdToDelete !== null) {

        setObjectIdToDelete(null);
        setDeleteDialogOpen(false);
    }    
    };
    
    const handleDeleteCancel = () => {
        setObjectIdToDelete(null); 
        setDeleteDialogOpen(false);
        navigate(`/tenant/mytenant`);
    };

    const handleEdit = (value) => {
        if (value  > 0) {
            navigate(`/tenant-update`);
        }
    };

    const handleValidate = (value) => {
        if (value  > 0) {
            navigate(`/mytenant-validate/${value}`);
        };
    };

    const handleCheckFirstAuditDate = (firstAuditDate) =>
    {
        var dt = new Date();
        dt.setDate(dt.getDate() - 7);
        dt = formatDate(dt);
        if (firstAuditDate > dt)
            return true;
        else
            return false;
    }


    if (accessDenied)
    {
        return(<AccessDenied></AccessDenied>)
    }
    
    if (serviceIsDownError) {
        return (<><ServiceIsDown></ServiceIsDown></>);
    }

    if (tenant)
    {
        return (
        <>
            <Card
            variant="outlined"
            sx={{
                width: '100%',
                maxWidth: 700,
            }}
            >
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <ProgressBar message={loadingMsg} loading={loading} />

                <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                <Table size="small" aria-label="tenant details table">
                    <TableBody>
                    <TableRow>
                        <TableCell
                        sx={{
                            width: { xs: '45%', sm: '40%' },
                            verticalAlign: 'top',
                            px: { xs: 1, sm: 2 },
                        }}
                        >
                        <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                            Tenant Id:
                        </Typography>
                        </TableCell>
                        <TableCell
                        sx={{
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            px: { xs: 1, sm: 2 },
                        }}
                        >
                        <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                            {tenant.id}
                        </Typography>
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell
                        sx={{
                            width: { xs: '45%', sm: '40%' },
                            verticalAlign: 'top',
                            px: { xs: 1, sm: 2 },
                        }}
                        >
                        <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                            Preserve SharePoint Audit:
                        </Typography>
                        </TableCell>
                        <TableCell sx={{ px: { xs: 1, sm: 2 } }}>
                        <Box
                            sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 1,
                            }}
                        >
                            {tenant.logSharePointAudit ? (
                            <TaskAltIcon fontSize="small" sx={{ color: 'green' }} />
                            ) : (
                            <RadioButtonUncheckedIcon fontSize="small" />
                            )}

                            {tenant.enableIncludeSharePointOneDriveAuditLogs && (
                            <Tooltip title="OneDrive Audit Logs are Enabled">
                                <Box component="span" sx={{ display: 'inline-flex' }}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 -5 32 32"
                                    id="onedrive"
                                >
                                    <path
                                    fill="green"
                                    d="M11.953 23.012c-1.393-.348-2.169-1.455-2.172-3.099-.001-.525.037-.777.168-1.115.321-.828 1.173-1.453 2.292-1.683.557-.114.729-.237.729-.522 0-.089.066-.355.147-.591.368-1.07 1.05-1.963 1.779-2.328.763-.382 1.147-.468 2.07-.463 1.31.007 1.964.291 2.878 1.251l.503.528.45-.156c2.18-.754 4.353.53 4.528 2.676l.048.587.429.154c1.226.439 1.802 1.361 1.698 2.717-.068.887-.483 1.595-1.14 1.946l-.309.165-6.861.013c-5.272.01-6.948-.009-7.238-.081zm-5.144-.947c-.813-.193-1.675-.912-2.079-1.733-.229-.466-.241-.535-.241-1.359 0-.784.02-.91.202-1.299.385-.821 1.122-1.414 2.047-1.647.195-.049.379-.128.408-.174s.061-.301.072-.566c.064-1.642 1.142-3.089 2.646-3.554.813-.251 1.834-.189 2.718.164.28.112.249.136.842-.641.351-.46 1.059-1.032 1.639-1.323.626-.314 1.277-.459 2.055-.457 2.176.005 4.051 1.366 4.744 3.443.222.664.21.849-.051.855-.114.002-.441.065-.726.139l-.519.135-.473-.473c-1.335-1.335-3.513-1.623-5.364-.71a4.454 4.454 0 0 0-1.783 1.57c-.32.486-.728 1.394-.728 1.619 0 .16-.129.24-.682.421-1.711.561-2.709 1.857-2.709 3.515 0 .604.156 1.342.371 1.765.081.16.127.311.101.337-.066.066-2.194.044-2.489-.026z"
                                    />
                                </svg>
                                </Box>
                            </Tooltip>
                            )}

                            {tenant.enableFilterBySiteCollectionOnSpLogs && (
                            <Tooltip title="Import Audit Logs By Site Collection(s) Enabled">
                                <PlaylistAddCheckIcon sx={{ color: 'green' }} />
                            </Tooltip>
                            )}
                        </Box>
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell sx={{ width: { xs: '45%', sm: '40%' }, px: { xs: 1, sm: 2 } }}>
                        <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                            Preserve Exchange Audit:
                        </Typography>
                        </TableCell>
                        <TableCell sx={{ px: { xs: 1, sm: 2 } }}>
                        {tenant.logExchangeAudit ? (
                            <TaskAltIcon fontSize="small" sx={{ color: 'green' }} />
                        ) : (
                            <RadioButtonUncheckedIcon fontSize="small" />
                        )}
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell sx={{ width: { xs: '45%', sm: '40%' }, px: { xs: 1, sm: 2 } }}>
                        <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                            Preserve Entra ID Audit:
                        </Typography>
                        </TableCell>
                        <TableCell sx={{ px: { xs: 1, sm: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {tenant.logEntraIDAudit ? (
                                <TaskAltIcon fontSize="small" sx={{ color: 'green' }} />
                            ) : (
                                <RadioButtonUncheckedIcon fontSize="small" />
                            )}
                            {tenant.onlyPreserveEntraGroupAuditLogs && (
                                <Tooltip title="Only the supported Security Group and Microsoft 365 Group audit operations will be preserved. All other Entra ID audit logs will be excluded.">
                                    <AnnouncementIcon fontSize="small" sx={{ color: 'green' }} />
                                </Tooltip>
                            )}
                        </Box>
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell sx={{ width: { xs: '45%', sm: '40%' }, px: { xs: 1, sm: 2 } }}>
                        <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                            Preserve Teams Audit:
                        </Typography>
                        </TableCell>
                        <TableCell sx={{ px: { xs: 1, sm: 2 } }}>
                        {tenant.logTeamsAudit ? (
                            <TaskAltIcon fontSize="small" sx={{ color: 'green' }} />
                        ) : (
                            <RadioButtonUncheckedIcon fontSize="small" />
                        )}
                        </TableCell>
                    </TableRow>
                    </TableBody>
                </Table>
                </TableContainer>
            </CardContent>

            <CardActions
                sx={{
                px: { xs: 1.5, sm: 2 },
                pb: { xs: 1.5, sm: 2 },
                pt: 0,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 1,
                }}
            >
                <Box
                sx={{
                    width: { xs: '100%', sm: '50%' },
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    justifyContent: { xs: 'flex-start', sm: 'flex-start' },
                }}
                >
                <Button size="small" variant="contained" onClick={() => handleEdit(tenant.id)}>
                    Edit
                </Button>
                {user.isSystemAdmin && (
                    <Button size="small" onClick={() => handleDeleteClick(tenant.id)}>
                    Delete
                    </Button>
                )}
                </Box>

                <Box
                sx={{
                    width: { xs: '100%', sm: '50%' },
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                }}
                >
                {tenant.firstAuditImportStage == 0 && (
                    <Button size="small" onClick={() => handleValidate(tenant.id)}>
                    Validate
                    </Button>
                )}
                {tenant.firstAuditImportStage == 1 && (
                    <Button size="small" onClick={() => handleValidate(tenant.id)}>
                    View Import Status
                    </Button>
                )}
                {tenant.firstAuditImportStage == 2 &&
                    handleCheckFirstAuditDate(tenant.firstAuditImportCompleteDate) && (
                    <Button size="small" variant="outlined" onClick={() => handleValidate(tenant.id)}>
                        View First Import Stats
                    </Button>
                    )}
                </Box>
            </CardActions>
            </Card>

            <TenantDeleteConfirmationDialog
            isOpen={isDeleteDialogOpen}
            onClose={handleDeleteCancel}
            user={user}
            setCompany={setCompany}
            companyId={tenant.companyId}
            tenantId={tenant.id}
            onDelete={handleDeleteConfirm}
            snackbarNotification={setOpenSnackbarNotification}
            />

            <Snackbar
            open={openSnackbarNotification}
            autoHideDuration={6000}
            onClose={handleSnackbarNotificationClose}
            message="Tenant Deleted"
            action={openSnackbarNotificationAction}
            />
        </>
        );
    };
}
